import React, { useState, useEffect } from 'react'
import SmartEntityProvider from 'contexts/smart-entity'
import { BlobProvider, Document, Page, View, Image, Text } from '@react-pdf/renderer'
import { resolveEmoji } from 'state/emoji/helpers'
import { styles } from './s'
import Content from './content'

const PDFContent = ({ resolve, content, ...props }) => {
    const [emoji, setEmoji] = useState(getContentEmojis({ content }))
    const [emojiResolved, setEmojiResolved] = useState(!emoji.some(({ emoji }) => !emoji))

    // This is async, meaning we have no choice but to handle it in a staggered
    // fashion like this, but it’s pretty instantaneous regardless.
    useEffect(() => {
        if(!emojiResolved) {
            (async () => {
                const resolvedEmoji = await resolveEmoji(emoji)

                setEmoji(resolvedEmoji)
                setEmojiResolved(true)
            })()
        }
    }, [emoji, emojiResolved])

    if(!emojiResolved) {
        return null
    }

    return (
        <BlobProvider document={(
            <Document style={{ marginVertical: 20 }}>
                {props?.frontPage}
                <Render
                    {...props}
                    content={content}
                    emoji={emoji} />
            </Document>
        )}>
            {({ blob, error }) => {
                if(blob) {
                    resolve([blob, null])
                }

                if(error) {
                    resolve([null, error])
                }

                return null
            }}
        </BlobProvider>
    )
}

export const SmartProvidedPDFContent = ({ id, ...props }) => (
    <SmartEntityProvider
        id={id}
        context="document"
        mode="fill">
        {({ entity, instated, interpolate, getVariableData }) => {
            if(!instated) {
                return null
            }

            return (
                <PDFContent
                    {...props}
                    {...entity}
                    interpolate={interpolate}
                    getVariableData={getVariableData}
                    id={id}
                    salt="smart" />
            )
        }}
    </SmartEntityProvider>
)

const Render = ({
    content, emoji, id, salt,
    interpolate, getVariableData,
    frontPage = null, logo = null
}) => content.map((section, index) => {
    if(typeof interpolate === 'function') {
        section.content = interpolate(section.content).content
    }

    return (
        <Page
            size="A4"
            style={styles.page}
            key={`${salt}:${id}:${index}`}>
            {(logo && !frontPage) && (
                <View style={styles.logoWrapper}>
                    <Image
                        style={styles.logo}
                        src={logo['4:1-medium'].url} />
                </View>
            )}
            <Content
                {...section}
                emoji={emoji}
                getVariableData={getVariableData} />
            <Text
                style={styles.pageNumber}
                render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
                fixed={true} />
        </Page>
    )
})

const getContentEmojis = ({ content }) => {
    content = structuredClone(content)

    if(!!content?.type && !content?.content) {
        content.content = []
    }

    if(Array.isArray(content)) {
        content = { content }
    }

    const contentReducer = (accumulator, node) => {
        if(node.type !== 'emoji') {
            return {
                ...accumulator,
                ...node.content?.reduce(contentReducer, accumulator)
            }
        }

        const name = node.attrs.name
        const emoji = node.attrs.emoji

        return {
            ...accumulator,
            [name]: {
                ...(accumulator[name] ?? null),
                name,
                emoji
            }
        }
    }

    return Object.values(content.content.reduce((accumulator, block) => ({
        ...accumulator,
        ...block.content?.reduce(contentReducer, accumulator)
    }), {}))
}

export default PDFContent